import React from 'react'
import ReactDOM from 'react-dom'
import styles from './Modal.module.css'
import CloseIcon from '../icons/Close'

interface Props {
  active: boolean
  withClose?: boolean
  onCancel: () => void
  onConfirm?: () => void
  alignItems?: string
  maxWidth?: string
  overflow?: string
  children: React.ReactNode
  zIndex?: string
}

function Modal({
  active,
  withClose = true,
  onCancel,
  onConfirm,
  alignItems = 'center',
  maxWidth = 'none',
  overflow = 'auto',
  zIndex,
  children,
}: Props) {
  const [container, setContainer] = React.useState<HTMLElement | null>(null)
  const [content, setContent] = React.useState<HTMLElement | null>(null)
  const [cancelButton, setCancelButton] = React.useState<HTMLElement | null>(null)
  const [modal, setModal] = React.useState<HTMLElement | null>(null)
  const mouseDownStartedInside = React.useRef(false)

  React.useEffect(() => {
    const div = document.createElement('div')
    setModal(div)
  }, [])

  React.useEffect(() => {
    if (typeof document === 'object' && modal) {
      document.body.appendChild(modal)
    }
    return function cleanup() {
      if (typeof document === 'object' && modal) {
        document.body.removeChild(modal)
      }
    }
  }, [modal])

  React.useEffect(() => {
    if (cancelButton !== null) {
      cancelButton.focus()
    }
  }, [cancelButton])

  const containerRef = React.useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      setContainer(node)
    }
  }, [])
  const contentRef = React.useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      setContent(node)
    }
  }, [])

  const onContainerMouseDown = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as Node
    if (content && content.contains(target)) {
      mouseDownStartedInside.current = true
    } else {
      mouseDownStartedInside.current = false
    }
  }, [content])

  const cancelRef = React.useCallback((node: HTMLElement | null) => {
    if (node !== null) {
      setCancelButton(node)
    }
  }, [])

  function onClick(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation()
    const target = e.target as Node
    // If the mouseDown started inside the content but the mouseup/click
    // happened outside (drag-release), don't treat it as an outside click.
    if (mouseDownStartedInside.current && content && !content.contains(target)) {
      mouseDownStartedInside.current = false
      return
    }

    if (
      (target !== cancelButton &&
        !cancelButton?.contains(target) &&
        content?.contains(target)) ||
        target === content
    ) {
      mouseDownStartedInside.current = false
      return
    }
    e.preventDefault()
    if(onCancel && typeof onCancel === 'function') {
      onCancel()
    }
  }
  function onBlur(event: React.FocusEvent<HTMLInputElement, Element>) {
    if (container === null) {
      return
    }

    const related = event.relatedTarget as Node | null
    if (related === null) return
    if (onCancel && typeof onCancel === 'function' && !container.contains(related)) {
      cancelButton?.focus()
    }
  }
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape' && onCancel && typeof onCancel === 'function') {
      onCancel()
    } else if (e.target === cancelButton && e.key === 'Enter') {
      e.preventDefault()
      if (onConfirm && typeof onConfirm === 'function') {
        onConfirm()
      }
    }
  }

  return modal
    ? ReactDOM.createPortal(
      !active ? null : (
        <div
          className={styles.modal}
          style={{
            alignItems: alignItems,
            zIndex: zIndex
          }}
          onClick={onCancel && typeof onCancel === 'function' ? onClick : () => null}
          onMouseDown={onContainerMouseDown}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          ref={containerRef}
        >
          <div
            className={styles.container}
            style={{
              maxWidth: maxWidth,
              overflow: overflow
            }}
            ref={contentRef}
          >
            {onCancel && withClose && typeof onCancel === 'function' && (
              <button className={styles.closeModalButton} style={{ zIndex: 120 }} onClick={() => onCancel()} type='button' ref={cancelRef}>
                <CloseIcon className={styles.closeIcon} color='#cf2e05' />
              </button>
            )}
            {children}
          </div>
        </div>
      ),
      modal,
    )
    : null
}

export default Modal
